import React from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  DocumentTextIcon,
  BellIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  HomeIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { CheckIcon } from '@heroicons/react/24/solid';
import api from '../../services/api';
import { format, differenceInDays, parseISO } from 'date-fns';
import { ko } from 'date-fns/locale';
import useAuthStore from '../../store/authStore';

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // 내 프로젝트
  const { data: myProjects } = useQuery(
    'customerProjects',
    () => api.get('/projects?my=true').then(res => res.data)
  );

  // 최근 프로젝트 (가장 최근 프로젝트 하나)
  const latestProject = myProjects?.projects?.[0];
  
  // 프로젝트 상세 정보 (최근 프로젝트가 있을 때만)
  const { data: projectDetail } = useQuery(
    ['projectDetail', latestProject?.id],
    () => api.get(`/projects/${latestProject.id}`).then(res => res.data),
    { enabled: !!latestProject?.id }
  );

  // 최근 활동 조회
  const { data: recentActivities } = useQuery(
    ['recentActivities', latestProject?.id],
    () => api.get(`/projects/${latestProject.id}/activities`).then(res => res.data),
    { enabled: !!latestProject?.id }
  );

  // 공지사항 조회
  const { data: notices } = useQuery(
    'notices',
    () => api.get('/notices?limit=3').then(res => res.data)
  );

  const handleInquiry = () => {
    if (latestProject?.id) {
      navigate(`/projects/${latestProject.id}?tab=chat`);
    }
  };

  // 프로젝트 상태 단계
  const getProjectSteps = () => {
    const baseSteps = [
      { key: 'received', label: '접수', icon: CheckIcon },
      { key: 'review', label: '검토', icon: CheckIcon },
      { key: 'contract', label: '계약', icon: CheckIcon },
      { key: 'completed', label: '완료', icon: CheckIcon }
    ];
    
    // 취소 상태일 때만 취소 단계 추가
    if (latestProject?.status === 'cancelled') {
      return [
        { key: 'received', label: '접수', icon: CheckIcon },
        { key: 'review', label: '검토', icon: CheckIcon },
        { key: 'cancelled', label: '취소', icon: CheckIcon, isCancel: true },
        { key: 'completed', label: '완료', icon: CheckIcon, disabled: true }
      ];
    }
    
    return baseSteps;
  };
  
  const projectSteps = getProjectSteps();

  // 현재 프로젝트 상태에 따라 단계 업데이트
  const getCurrentStep = () => {
    if (!latestProject) return projectSteps;
    
    const statusOrder = ['received', 'review', 'contract', 'completed'];
    const currentIndex = statusOrder.indexOf(latestProject.status);
    
    return projectSteps.map((step, index) => ({
      ...step,
      completed: !step.isCancel && !step.disabled && index <= currentIndex,
      current: step.key === latestProject.status
    }));
  };

  const steps = getCurrentStep();
  
  // 진행률 계산
  const getProgress = () => {
    if (!latestProject) return 0;
    if (latestProject.status === 'cancelled') return 0;
    
    const statusProgress = {
      'received': 25,
      'review': 50,
      'contract': 75,
      'progress': 85,
      'completed': 100
    };
    
    return statusProgress[latestProject.status] || 0;
  };
  
  // D-Day 계산
  const getDDay = () => {
    if (!projectDetail?.estimated_completion_date) return null;
    const dDay = differenceInDays(parseISO(projectDetail.estimated_completion_date), new Date());
    if (dDay > 0) return `D-${dDay}`;
    if (dDay === 0) return 'D-Day';
    return `D+${Math.abs(dDay)}`;
  };
  
  const progressPercent = getProgress();
  const dDayText = getDDay();

  // 시간대별 인사말
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 6) return '안녕하세요';
    else if (hour < 12) return '좋은 아침이에요';
    else if (hour < 17) return '좋은 오후에요';
    else if (hour < 22) return '좋은 저녁이에요';
    else return '안녕하세요';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* 헤더 - 더 넓고 세련된 디자인 */}
      <div className="mx-1 mt-3 bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-700 rounded-xl shadow-lg">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white font-bold text-lg">
                {getGreeting()}, {user?.name || '고객'}님
              </h1>
              <p className="text-blue-100 text-sm mt-1">
                프로젝트가 검토 단계입니다
              </p>
            </div>
            <div className="text-right">
              <p className="text-blue-200 text-xs">고객센터</p>
              <p className="text-white font-bold text-lg">1566-0622</p>
            </div>
          </div>
        </div>
      </div>

      <div className="px-1 py-4 space-y-4">
        {/* 메인 프로젝트 카드 */}
        {latestProject && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {/* 프로젝트 헤더 */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-5">
              <h2 className="text-white font-bold text-xl">{latestProject.title}</h2>
              <div className="flex items-center gap-4 mt-3">
                <div className="flex items-center gap-1.5">
                  <MapPinIcon className="h-4 w-4 text-blue-100" />
                  <span className="text-sm text-blue-50">{latestProject.address}</span>
                </div>
                {projectDetail?.contractor && latestProject.status !== 'cancelled' && (
                  <div className="flex items-center gap-1.5">
                    <UserIcon className="h-4 w-4 text-blue-100" />
                    <span className="text-sm text-blue-50">
                      {projectDetail.contractor.name} 소장
                    </span>
                  </div>
                )}
              </div>
              
              {/* 진행률 표시 */}
              {latestProject.status !== 'cancelled' && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-blue-100">진행률</span>
                    <span className="text-base font-bold text-white">{progressPercent}%</span>
                  </div>
                  <div className="w-full bg-blue-900/40 rounded-full h-2.5">
                    <div 
                      className="bg-gradient-to-r from-white to-blue-200 rounded-full h-2.5 transition-all duration-500 shadow-sm"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                </div>
              )}
              
            </div>

            {/* 취소 상태 경고 */}
            {latestProject.status === 'cancelled' && (
              <div className="bg-red-50 px-4 py-3 border-t border-red-100">
                <p className="text-base font-semibold text-red-800">프로젝트가 취소되었습니다</p>
                <p className="text-sm text-red-600 mt-1">보증서가 폐기되었습니다</p>
              </div>
            )}

            {/* 진행 단계 */}
            <div className="px-4 py-4 bg-gradient-to-b from-gray-50 to-white">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <div key={step.key} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`
                        w-12 h-12 rounded-full flex items-center justify-center text-sm font-semibold transition-all
                        ${step.isCancel && step.current
                          ? 'bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg'
                          : step.completed 
                          ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-md' 
                          : step.disabled
                          ? 'bg-gray-200 text-gray-400'
                          : 'bg-white border-2 border-gray-300 text-gray-500'
                        }
                      `}>
                        {step.completed && !step.isCancel ? 
                          <CheckIcon className="h-6 w-6" /> : 
                          step.isCancel ? '✕' : index + 1
                        }
                      </div>
                      <span className={`mt-2 text-sm font-medium ${
                        step.isCancel && step.current ? 'text-red-600' : 
                        step.completed ? 'text-blue-600' : 
                        'text-gray-500'
                      }`}>
                        {step.label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`h-0.5 flex-1 -mt-6 mx-1 ${
                        steps[index + 1].completed || steps[index + 1].current
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600' 
                          : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
              
              {/* 최근 업데이트 */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  최근 업데이트: {format(new Date(latestProject.updated_at || latestProject.created_at), 'M월 d일 HH:mm', { locale: ko })}
                </p>
              </div>
            </div>
            
            {/* D-Day 표시 */}
            {dDayText && latestProject.status !== 'cancelled' && latestProject.status !== 'completed' && (
              <div className="px-4 py-3 border-t border-blue-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center justify-between">
                  <span className="text-base text-gray-700">완료 예정일</span>
                  <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">{dDayText}</span>
                </div>
              </div>
            )}
            
            {/* 프로젝트 상세보기 버튼 - 카드 하단에 통합 */}
            <Link
              to={`/projects/${latestProject.id}`}
              className="block w-full bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white px-4 py-4 text-center font-bold text-base transition-all hover:shadow-lg relative overflow-hidden group"
            >
              <div className="relative z-10 flex items-center justify-center gap-3">
                <span className="text-white">프로젝트 상세보기</span>
                <ChevronRightIcon className="h-5 w-5 text-white animate-pulse" />
              </div>
              {/* 움직이는 광택 효과 */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
            </Link>
          </div>
        )}

        {/* 빠른 메뉴 */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={handleInquiry}
            className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all text-left hover:scale-[1.02] border border-gray-100"
          >
            <div className="flex flex-col">
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-3 shadow-md">
                <ChatBubbleLeftRightIcon className="h-7 w-7 text-white" />
              </div>
              <p className="font-bold text-gray-900 text-base">소통하기</p>
              <p className="text-sm text-gray-600 mt-1">소장님과 실시간 대화</p>
            </div>
          </button>

          {projectDetail?.contractor?.phone && latestProject?.status !== 'cancelled' ? (
            <a
              href={`tel:${projectDetail.contractor.phone}`}
              className="bg-white rounded-xl p-4 shadow-md hover:shadow-xl transition-all block hover:scale-[1.02] border border-gray-100"
            >
              <div className="flex flex-col">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mb-3 shadow-md">
                  <PhoneIcon className="h-7 w-7 text-white" />
                </div>
                <p className="font-bold text-gray-900 text-base">전화 연결</p>
                <p className="text-sm text-gray-600 mt-1">담당 소장님</p>
              </div>
            </a>
          ) : (
            <div className="bg-gray-100 rounded-xl p-4 opacity-60 border border-gray-200">
              <div className="flex flex-col">
                <div className="w-14 h-14 bg-gray-300 rounded-xl flex items-center justify-center mb-3">
                  <PhoneIcon className="h-7 w-7 text-gray-500" />
                </div>
                <p className="font-bold text-gray-600 text-base">전화 연결</p>
                <p className="text-sm text-gray-500 mt-1">담당 소장 미배정</p>
              </div>
            </div>
          )}
        </div>

        {/* 건기넷 & 건기폴리싱 선택의 이유 */}
        <div className="bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 rounded-xl p-5 text-white shadow-xl">
          <h3 className="font-bold text-lg mb-4">건기넷 & 건기폴리싱 선택의 이유</h3>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/15 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="flex items-start gap-2.5">
                <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold">투명한 계약체계</p>
                  <p className="text-xs text-blue-200 mt-1">모든 계약 온라인으로 투명하게 관리</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/15 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="flex items-start gap-2.5">
                <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold">실시간 소통</p>
                  <p className="text-xs text-blue-200 mt-1">24시간 대화 가능, 모든 기록 보존</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/15 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="flex items-start gap-2.5">
                <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold">보증서 발급</p>
                  <p className="text-xs text-blue-200 mt-1">시공 완료 후 품질보증서 발급</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white/15 rounded-lg p-3 backdrop-blur-sm border border-white/10">
              <div className="flex items-start gap-2.5">
                <CheckCircleIcon className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold">책임 시공</p>
                  <p className="text-xs text-blue-200 mt-1">15년 전통의 검증된 시공 품질</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;